import { initializeApp } from 'firebase/app'
import type { FirebaseApp } from 'firebase/app'
import { getDatabase } from 'firebase/database'
import type { Database } from 'firebase/database'
import { getFirestore } from 'firebase/firestore'
import type { Firestore } from 'firebase/firestore'
import { firebaseConfig } from '../config/firebase.config'

interface FirebaseStateType {
  app: FirebaseApp | null
  db: Firestore | null
  realtimeDb: Database | null
  firebaseInitialized: boolean
  firebaseError: Error | null
}

const firebaseState: FirebaseStateType = {
  app: null,
  db: null,
  realtimeDb: null,
  firebaseInitialized: false,
  firebaseError: null,
}

try {
  firebaseState.app = initializeApp(firebaseConfig)
  firebaseState.db = getFirestore(firebaseState.app)
  firebaseState.realtimeDb = getDatabase(firebaseState.app)
  firebaseState.firebaseInitialized = true
} catch (error) {
  firebaseState.firebaseError =
    error instanceof Error
      ? error
      : new Error('Unknown Firebase initialization error')
  console.error('Firebase initialization failed:', firebaseState.firebaseError)
  console.warn('Application will continue to run without Firebase features')
}

export const isFirebaseAvailable = (): boolean => {
  return firebaseState.firebaseInitialized && firebaseState.app !== null
}

export const getFirebaseError = (): Error | null => {
  return firebaseState.firebaseError
}

export const getFirebaseApp = (): FirebaseApp | null => {
  if (!isFirebaseAvailable()) {
    console.warn('Firebase is not available. Please check your configuration.')
    return null
  }
  return firebaseState.app
}

export const getFirebaseDb = (): Firestore | null => {
  if (!isFirebaseAvailable()) {
    console.warn(
      'Firestore is not available. Please check your Firebase configuration.'
    )
    return null
  }
  return firebaseState.db
}

export const getRealtimeDb = (): Database | null => {
  if (!isFirebaseAvailable()) {
    console.warn(
      'Realtime Database is not available. Please check your Firebase configuration.'
    )
    return null
  }
  return firebaseState.realtimeDb
}

export const db = firebaseState.db
export const realtimeDb = firebaseState.realtimeDb
export const firebaseInitialized = firebaseState.firebaseInitialized
export const app = firebaseState.app
export default firebaseState.app
